/**
 * EligibilityReviewCard — the lead-profile home of the AI medical history
 * review (Profile tab, directly below MedicalRecordsCard). Lists the LATEST
 * review per study (result badge, date, "View results" link, + how many past
 * runs exist); in-flight rows show a spinner with the pipeline status. The
 * "Conduct AI medical history review" button opens the study picker modal.
 *
 * Reads GET /contacts/:id/eligibility-reviews; polls it every 4s while any
 * review is non-terminal (StudyDetail precedent) and refetches on the parent's
 * debounced Realtime pingTick. Non-terminal reviews older than 30 minutes are
 * treated as STALLED: the poll drops to 60s and the row says so instead of
 * spinning forever. Queued reviews get a Cancel action (queued-only
 * server-side); rows whose latest review carries human overrides show an
 * "Overridden" chip because the AI result badge may not reflect them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import api from '../../../services/api';
import { EligibilityOverallResult, EligibilityReview, EligibilityReviewStatus } from '../../types';
import { relTime } from '../../lib';
import { useConfirm } from '../ui';
import StudyPickerModal from './StudyPickerModal';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const TERMINAL_STATUSES = new Set<EligibilityReviewStatus>(['completed', 'failed', 'cancelled']);

/** A non-terminal review older than this is considered stalled. */
const STALL_MS = 30 * 60 * 1000;

const isStalledReview = (r: EligibilityReview, now: number): boolean => {
  if (TERMINAL_STATUSES.has(r.status)) return false;
  const t = new Date(r.created_at).getTime();
  return Number.isFinite(t) && now - t > STALL_MS;
};

const STATUS_LABELS: Record<EligibilityReviewStatus, string> = {
  queued: 'Queued',
  building_dossier: 'Building dossier',
  analyzing: 'Analyzing criteria',
  verifying: 'Verification pass',
  // Facts engine (engine==='facts'|'shadow') in-flight states.
  extracting_facts: 'Extracting facts…',
  evaluating: 'Evaluating criteria…',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const RESULT_BADGES: Record<EligibilityOverallResult, { label: string; cls: string }> = {
  eligible: {
    label: 'Potentially eligible',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  },
  excluded: {
    label: 'Excluded',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  review_required: {
    // Impact-aware wording — matches the results page's NEEDS REVIEW verdicts.
    label: 'Needs review',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  incomplete: {
    label: 'Incomplete',
    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
};

function reviewBadge(r: EligibilityReview): { label: string; cls: string } {
  if (r.status === 'completed' && r.overall_result) {
    return RESULT_BADGES[r.overall_result] || RESULT_BADGES.incomplete;
  }
  if (r.status === 'failed') {
    return { label: 'Failed', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
  }
  if (r.status === 'cancelled') {
    return { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
  }
  return {
    label: STATUS_LABELS[r.status] || r.status,
    cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  };
}

interface Props {
  contactId: string;
  /** Debounced Realtime ping from ContactDetail — silent refetch. */
  pingTick?: number;
  /** The contact's Considering study — pre-checked in the picker when set. */
  primaryStudyId?: string | null;
  /** Rendered inside ProfileTab's frameless grouped list, which owns the
   *  divide-y chrome: drop this card's own border/background/shadow. Absent =
   *  the standalone card, byte for byte what renders today. */
  frameless?: boolean;
}

export default function EligibilityReviewCard({ contactId, pingTick = 0, primaryStudyId = null, frameless = false }: Props) {
  const confirm = useConfirm();
  const [reviews, setReviews] = useState<EligibilityReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Live clock for stall detection — advanced on every poll tick. */
  const [now, setNow] = useState<number>(() => Date.now());
  const [cancelBusy, setCancelBusy] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/eligibility-reviews`);
      setReviews(res.data?.reviews || []);
      setError(null);
    } catch (e: any) {
      if (initial) setError(errMsg(e));
    } finally {
      if (initial) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { if (pingTick) load(false); }, [pingTick, load]);

  // Poll every 4s while any review is still running; once every running
  // review is stalled (>30 min old) drop to a slow 60s poll instead of
  // hammering the endpoint forever; clear entirely when nothing runs. The
  // interval is torn down on unmount and whenever contactId changes (load is
  // contactId-bound).
  const anyRunning = useMemo(() => reviews.some((r) => !TERMINAL_STATUSES.has(r.status)), [reviews]);
  const anyFreshRunning = useMemo(
    () => reviews.some((r) => !TERMINAL_STATUSES.has(r.status) && !isStalledReview(r, now)),
    [reviews, now]);
  useEffect(() => {
    if (!anyRunning) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      load(false);
    }, anyFreshRunning ? 4000 : 60000);
    return () => window.clearInterval(timer);
  }, [anyRunning, anyFreshRunning, load]);

  const cancelReview = async (reviewId: string) => {
    if (cancelBusy) return;
    if (!(await confirm({
      title: 'Cancel this queued AI review?',
      message: 'It has not started running and nothing will be billed.',
      confirmLabel: 'Cancel review',
      cancelLabel: 'Keep it',
      danger: true,
    }))) return;
    setCancelBusy(reviewId);
    setCancelError(null);
    try {
      await api.post(`/api/crm/eligibility-reviews/${reviewId}/cancel`, {});
      await load(false);
    } catch (e: any) {
      // 409 = the queued-only guard: the review started while the confirm
      // dialog was open — refresh so the row shows the real status.
      setCancelError(
        e?.response?.status === 409
          ? (e?.response?.data?.detail || 'Only queued reviews can be cancelled — this one has already started.')
          : errMsg(e));
      await load(false);
    } finally {
      setCancelBusy(null);
    }
  };

  // The list is newest-first: the first row per study is its latest review.
  const perStudy = useMemo(() => {
    const map = new Map<string, { latest: EligibilityReview; count: number }>();
    for (const r of reviews) {
      const entry = map.get(r.study_id);
      if (!entry) map.set(r.study_id, { latest: r, count: 1 });
      else entry.count += 1;
    }
    return Array.from(map.values());
  }, [reviews]);

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          AI eligibility reviews
        </h3>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          title="Run an AI review of everything on file against a study's criteria"
          className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Conduct AI medical history review
        </button>
      </div>

      <p className="mb-3 text-[11px] text-gray-400 dark:text-gray-500">
        AI-assisted pre-screening aid — not a medical or enrollment determination.
      </p>

      {error && (
        <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {cancelError && (
        <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {cancelError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : perStudy.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No AI reviews yet. Run one to check everything on file — records, calls, texts and
          forms — against a study's review criteria.
        </p>
      ) : (
        <ul className="space-y-2">
          {perStudy.map(({ latest, count }) => {
            const badge = reviewBadge(latest);
            const stalled = isStalledReview(latest, now);
            const running = !TERMINAL_STATUSES.has(latest.status) && !stalled;
            return (
              <li
                key={latest.study_id}
                className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 p-2.5 dark:border-gray-700/60"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {latest.study_title || 'Study'}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                      {running && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                      {badge.label}
                    </span>
                    {latest.has_overrides && (
                      <span
                        title="A human overrode one or more criterion results — the AI result badge may not reflect the overrides. Open the results for the human determinations."
                        className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                      >
                        Overridden
                      </span>
                    )}
                    <span className="text-[11px] text-gray-400">{relTime(latest.created_at)}</span>
                    {count > 1 && (
                      <span className="text-[11px] text-gray-400">{count} runs</span>
                    )}
                  </div>
                  {stalled && (
                    <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      This review appears stalled — it started over 30 minutes ago and has not completed.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {latest.status === 'queued' && (
                    <button
                      type="button"
                      onClick={() => cancelReview(latest.id)}
                      disabled={cancelBusy !== null}
                      className="inline-flex min-h-[44px] items-center rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
                    >
                      {cancelBusy === latest.id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                  <Link
                    to={`/crm/contacts/${contactId}/reviews/${latest.id}`}
                    className="inline-flex min-h-[44px] items-center rounded-lg px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
                  >
                    View results
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pickerOpen && (
        <StudyPickerModal
          contactId={contactId}
          primaryStudyId={primaryStudyId}
          onClose={() => setPickerOpen(false)}
          onStarted={() => { setPickerOpen(false); load(false); }}
        />
      )}
    </div>
  );
}
